import React, { useEffect } from 'react'
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { RootState } from '@/store/store';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectInitialLoad } from '@/store/selectors';
import { logout } from '@/store/slices/authSlice';
import { clearUser } from '@/store/slices/userSlice';
import { setInitialLoad } from '@/store/slices/selectionSlice';
import FullPageLoader from './FullPageLoader';

const SetupLoaderComponent: React.FC = () => {
    const dispatch = useAppDispatch();
    const { logout: auth0Logout } = useAuth0();
    
    const auth = useSelector(
        (state: RootState) => state.auth
    );
    const users = useSelector(
        (state: RootState) => state.user
    );
    const customers = useSelector(
        (state: RootState) => state.customer
    );
    const sites = useSelector(
        (state: RootState) => state.site
    );
    const initialLoad = useAppSelector(selectInitialLoad);

    // Check if any API failed and redirect to login immediately
    useEffect(() => {
        const handleApiFailureRedirect = async (errorSource: string, errorMessage: string) => {
            console.error(`${errorSource} API failed:`, errorMessage);
            
            // Clear Redux state
            dispatch(logout());
            dispatch(clearUser());
            
            // Logout from Auth0 and redirect to login
            try {
                await auth0Logout({ 
                    logoutParams: { 
                        returnTo: window.location.origin 
                    } 
                });
            } catch (err) {
                console.error('Logout failed:', err);
                // Force reload to clear state
                window.location.href = window.location.origin;
            }        
        };

        if (auth.isLoggedIn && initialLoad) {
            // Check for user API failure
            if (users.status === 'failed' && users.error) {
                handleApiFailureRedirect('User', users.error);
            }
            // Check for customer API failure
            else if (customers.status === 'failed' && customers.error) {
                handleApiFailureRedirect('Customer', customers.error);
            }
            // Check for site API failure
            else if (sites.status === 'failed' && sites.error) {
                handleApiFailureRedirect('Site', sites.error);
            }
            // Check if both user and customer APIs have completed successfully
            // This includes when customers/site_groups arrays are empty
            else if (
                users.status === 'succeeded' && 
                customers.status === 'succeeded' && 
                users.fetched && 
                customers.fetched
            ) {
                // All required initial APIs have completed
                // Set initialLoad to false so the loader will dismiss
                console.log('SetupLoader: All required APIs completed. Setting initialLoad to false.');
                dispatch(setInitialLoad(false));
            }
        }
    }, [users.status, customers.status, sites.status, auth.isLoggedIn, initialLoad, dispatch, auth0Logout]);

    // Show loader only if users or customers are still loading
    // Sites are fetched on-demand after customer selection, not during initial setup
    // So we should NOT wait for sites to be fetched
    const isStillLoading = auth.isLoggedIn &&
        initialLoad &&
        (
            (!users.fetched && users.status !== 'failed') || 
            (!customers.fetched && customers.status !== 'failed')
        );

    console.log('SetupLoader - isStillLoading:', isStillLoading, {
        isLoggedIn: auth.isLoggedIn,
        initialLoad,
        usersFetched: users.fetched,
        usersStatus: users.status,
        customersFetched: customers.fetched,
        customersStatus: customers.status
    });

    return isStillLoading ? (
        <FullPageLoader message="Please wait while we set you up..." />
    ) : null;
}

export default SetupLoaderComponent
